import {type VariantProps, cva} from "class-variance-authority";
import * as React from "react";

import {cn} from "@/lib/utils";

const alertVariants = cva(
  "group/alert relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-1 has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        info: "bg-card text-blue-700 border-blue-400 [&>svg]:text-current *:data-[slot=alert-description]:text-blue-700/90",
        warning:
          "bg-card text-amber-700 border-amber-400 [&>svg]:text-current *:data-[slot=alert-description]:text-amber-700/90",
        destructive:
          "text-destructive border-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({variant}), className)}
      {...props}
    />
  );
}

function AlertTitle({className, ...props}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-1 line-clamp-1 min-h-4 font-medium tracking-tight group-has-[>svg]/alert:col-start-2",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({className, ...props}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground col-start-1 grid justify-items-start gap-1 text-sm group-has-[>svg]/alert:col-start-2 [&_p]:leading-relaxed",
        className,
      )}
      {...props}
    />
  );
}

export {Alert, AlertTitle, AlertDescription};
